import { Link, useLocation } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import DOMPurify from "dompurify";
import { ASSETS, PRACTITIONER } from "@/data/content";
import { useSite } from "@/context/SiteContext";
import RichText from "@/components/RichText";
import Snippet from "@/components/Snippet";

export default function Footer() {
  const { pathname } = useLocation();
  const { img, settings } = useSite();
  if (pathname.startsWith("/admin")) return null;

  const logo = img("logo", ASSETS.logo);
  const business = settings.business_name || PRACTITIONER.business;
  const email = settings.email || PRACTITIONER.email;
  const emailDisplay = settings.email_display || PRACTITIONER.emailDisplay || email;
  const phone = settings.phone || PRACTITIONER.phone;
  const location = settings.location || PRACTITIONER.location;

  return (
    <footer className="bg-brand-forest text-white/90 mt-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid gap-12 md:grid-cols-3">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <img src={logo} alt="" className="h-12 w-12 rounded-full object-cover bg-white" />
            <span className="font-heading text-2xl">{business}</span>
          </div>
          <p className="font-body text-sm text-white/70 leading-relaxed max-w-xs">
            <Snippet k="footer.tagline" fallback="Gentle Quantum Healing Hypnosis Technique sessions for women, offered with warmth and care in South Devon." />
          </p>
        </div>

        <div>
          <h4 className="font-heading text-xl mb-4 text-white"><Snippet k="footer.contact_heading" fallback="Get in touch" /></h4>
          <ul className="space-y-3 font-body text-sm text-white/80">
            <li className="flex items-center gap-3"><Mail size={16} strokeWidth={1.5} /> <a href={`mailto:${email}`} className="hover:text-white transition-colors">{emailDisplay}</a></li>
            <li className="flex items-center gap-3"><Phone size={16} strokeWidth={1.5} /> {phone}</li>
            <li className="flex items-center gap-3"><MapPin size={16} strokeWidth={1.5} /> {location}</li>
          </ul>
          <div className="mt-5 flex flex-wrap gap-4 font-body text-sm">
            <Link to="/book" className="underline underline-offset-4 hover:text-white"><Snippet k="footer.link_book" fallback="Book a session" /></Link>
            <Link to="/volunteer" className="underline underline-offset-4 hover:text-white"><Snippet k="footer.link_free" fallback="Free sessions" /></Link>
            <Link to="/contact" className="underline underline-offset-4 hover:text-white"><Snippet k="footer.link_contact" fallback="Contact" /></Link>
          </div>
        </div>

        <div>
          <h4 className="font-heading text-xl mb-4 text-white"><Snippet k="footer.note_heading" fallback="A gentle note" /></h4>
          <p className="font-body text-xs text-white/60 leading-relaxed">
            <Snippet k="footer.note" fallback="QHHT is a complementary wellbeing practice — not medical or psychological treatment. Please seek medical advice for any health concerns." />
          </p>
        </div>
      </div>

      <div className="border-t border-white/15">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <p className="font-body text-[11px] uppercase tracking-[0.22em] text-white/50 mb-3"><Snippet k="footer.disclaimer_heading" fallback="Disclaimer" /></p>
          {settings.disclaimer_html ? (
            <div
              className="space-y-3 font-body text-xs text-white/55 leading-relaxed [&_p]:!text-white/55 [&_p]:!text-xs [&_p]:!leading-relaxed"
              data-testid="footer-disclaimer"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(settings.disclaimer_html) }}
            />
          ) : (
            <div className="space-y-3 font-body text-xs text-white/55 leading-relaxed" data-testid="footer-disclaimer">
              <p>
                Isabelle Legér is a Level 2 QHHT Practitioner. She is not a certified psychologist, psychotherapist,
                healer, therapist, counsellor or doctor. She does not diagnose, prescribe or treat any mental or
                physical conditions.
              </p>
              <p>
                Hypnosis, which is self-hypnosis, can help and support a healing journey. It is not designed to replace
                any conventional forms of treatment. If you are currently in psychotherapy or following a specific
                course of treatment, it is your responsibility to consult your therapist, counsellor or doctor and check
                that hypnosis is suitable and safe for you to engage with.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-white/15">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 font-body text-xs text-white/60">
          <span>© {new Date().getFullYear()} {business}. <Snippet k="footer.copyright" fallback="Developed by Dolores Cannon. All rights reserved." /></span>
          <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-white"><Snippet k="footer.admin_link" fallback="Practitioner login" /></Link>
        </div>
      </div>
    </footer>
  );
}
